import { SellerGroup } from "../carrier-integration/types/seller-group";

export interface SellerGroupUserRole {
  id: string;
  name: string;
  seller_group_id: string;
  read_order: boolean;
  create_order: boolean;
  read_route: boolean;
  create_route: boolean;
  read_delivery: boolean;
  create_delivery: boolean;
  read_nps: boolean;
  read_driver: boolean;
  create_driver: boolean;
  update_route_driver: boolean;
  update_route_to_canceled: boolean;
  update_order_destination_address: boolean;
  update_order_customer: boolean;
  update_order_to_manual_handle: boolean;
  access_driver_route: boolean;
  update_order_takeout: boolean;
  read_dispatch_rules: boolean;
  update_dispatch_rules: boolean;
  read_resend_rules: boolean;
  update_resend_rules: boolean;
  read_sellers: boolean;
  update_sellers: boolean;
  read_order_integrations: boolean;
  update_order_integrations: boolean;
  read_carrier_integrations: boolean;
  update_carrier_integrations: boolean;
  read_report: boolean;
  create_report: boolean;
  update_order_status: boolean;
  read_inactivity_rules: boolean;
  update_inactivity_rules: boolean;
}

export type SellerGroupPermissions = Omit<
  SellerGroupUserRole,
  'name' | 'id' | 'seller_group_id'
>;

export type SellerGroupUserType = 'USER' | 'MASTER' | 'INTERNAL' | 'ADMIN';

export interface SellerGroupWhiteLabel {
  id: string;
  carrier_name: string;
  favicon: string;
  logo: string;
  logo_bg: string;
  primary: string;
}

export interface SellerGroupUserSeller {
  id: string;
  document_number: string;
  company_name: string;
  trading_name: string;
  created_at: string;
  white_label: SellerGroupWhiteLabel;
  identifier?: string;
  seller_group_id?: string;
  timezone?: string;
  operation_hours?: {
    [key: string]: {
      open: string;
      close: string;
    };
  } | null;
}

export enum SellerGroupUserPermissions {
  DASHBOARD = 'dashboard',
  ORDERS = 'orders',
  FREIGHT_TABLES = 'freight_tables',
  CARRIER_INTEGRATIONS = 'carrier_integrations',
  ORDER_INTEGRATIONS = 'order_integrations',
  CSAT = 'csat',
  REPORTS = 'reports',
  QUOTATIONS = 'quotations',
  QUOTATION_RULES = 'quotation_rules',
  DISPATCH_RULE_TO_CARRIER = 'dispatch_rule_to_carrier',
  RESEND_RULE_TO_CARRIER = 'resend_rule_to_carrier',
  INACTIVITY_RULE_TO_CARRIER = 'inactivity_rule_to_carrier',
  DISPATCH_RULE_TO_PRIVATE_FLEET = 'dispatch_rule_to_private_fleet',
  RESEND_RULE_TO_PRIVATE_FLEET = 'resend_rule_to_private_fleet',
  INACTIVITY_RULE_TO_PRIVATE_FLEET = 'inactivity_rule_to_private_fleet',
  DELIVERIES = 'deliveries',
  PAYMENTS = 'payments',
  SELLERS = 'sellers',
  USERS = 'users',
  DRIVERS = 'drivers',
  THEMES = 'themes',
  WEBHOOKS = 'webhooks',
  API = 'api',
  NOTIFICATIONS = 'notifications',
  MAIL_LABEL = 'mail_label',
  TRACKING = 'tracking',
  APPEARANCE = 'appearance',
  REGION = 'region',
  ROUTES_TO_CARRIER = 'routes_to_carrier',
  ROUTES_TO_PRIVATE_FLEET = 'routes_to_private_fleet',
  SPOT_TO_CARRIER = 'spot_to_carrier',
  RESEND_SPOT_TO_CARRIER = 'resend_spot_to_carrier',
  CROWDSHIPPING = 'crowdshipping',
  TAGS_RULE = 'tags_rule',
  STATUS_BAR_ORDER = 'status_bar_order',
  KPI_ORDER = 'kpi_order',
}

export declare enum FeatureModuleType {
  PROFILE = "PROFILE",
  HIRED_MODULE = "HIRED_MODULE"
}

export declare class SellerGroupFeatureModule {
  seller_group_id: string;
  feature_module_id: string;
  seller_group: SellerGroup;
  feature_module: FeatureModule;
}

export declare class FeatureModule {
  id: string;
  name: string;
  type: FeatureModuleType;
  dashboard: boolean;
  orders: boolean;
  routes_to_carrier: boolean;
  routes_to_private_fleet: boolean;
  freight_tables: boolean;
  carrier_integrations: boolean;
  order_integrations: boolean;
  csat: boolean;
  reports: boolean;
  quotations: boolean;
  quotation_rules: boolean;
  dispatch_rule_to_carrier: boolean;
  resend_rule_to_carrier: boolean;
  inactivity_rule_to_carrier: boolean;
  dispatch_rule_to_private_fleet: boolean;
  resend_rule_to_private_fleet: boolean;
  inactivity_rule_to_private_fleet: boolean;
  deliveries: boolean;
  payments: boolean;
  sellers: boolean;
  users: boolean;
  drivers: boolean;
  themes: boolean;
  webhooks: boolean;
  api: boolean;
  notifications: boolean;
  spot_to_carrier: boolean;
  resend_spot_to_carrier: boolean;
  crowdshipping: boolean;
  seller_group_feature_modules: SellerGroupFeatureModule[];
  seller_groups: SellerGroup[];
}

export interface HiredModule {
  feature_module: FeatureModule;
}

export interface SellerGroupUser {
  id: string;
  created_at: string;
  sellers: SellerGroupUserSeller[];
  is_seller_group_admin: boolean;
  seller_group_id: string;
  role: SellerGroupUserRole | null;
  type: SellerGroupUserType;
  email: string;
  seller_group: {
    id: string;
    name: string;
    feature_config?: Record<string, boolean>;
    is_homologation?: boolean;
    // Carrier Hub — true = tela legada (/carrier-integrations), false = Hub
    // novo. Ver dashboard-api/src/carrier-hub/carrier-hub-legacy-flag.store.ts
    // (fonte em memória enquanto a migration real não roda contra Postgres).
    is_legacy_integration?: boolean;
    // A API devolve `null` pra conta sem módulo configurado (sandbox, 20/10).
    // Leia via `hasProfile` (features/seller-group/user-access).
    profile_module: FeatureModule | null;
    hired_modules: HiredModule[];
  };
  profile_permissions: Record<SellerGroupUserPermissions, boolean>;
  hired_modules_permissions: Record<SellerGroupUserPermissions, boolean>;
  all_permissions: Record<SellerGroupUserPermissions, boolean>;
}

export interface SellerGroupSearchResponse<T> {
  total: number;
  data: T[];
}
